<template>
  <div class="p-6 bg-white/60 rounded-xl min-h-screen">
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-bold text-gray-800 inline-block transition-all duration-500">
        <span class="bg-clip-text text-transparent bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 hover:animate-pulse">
          بدون ارز
        </span>
      </h1>
      <div class="flex gap-3 mr-3">
        <button
            @click="refreshProducts"
            class="p-2 bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 hover:bg-gray-50 transform hover:scale-105"
            :disabled="listLoading"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24"
               stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="bg-white bg-opacity-70 backdrop-filter backdrop-blur-lg rounded-xl p-3 mb-6 shadow-sm border border-gray-100">
      <div class="flex flex-wrap items-center gap-2">
        <div class="relative w-32">
          <input v-model="filters.name_search" placeholder="نام"
                 class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border w-full text-right"
                 @keyup.enter="applyFilters">
        </div>

        <div class="relative w-32">
          <input v-model="filters.sku" placeholder="SKU"
                 class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border w-full text-right"
                 @keyup.enter="applyFilters">
        </div>

        <div class="relative w-32">
          <input v-model="filters.id_in" placeholder="شناسه"
                 class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border w-full text-right"
                 @keyup.enter="applyFilters">
        </div>

        <!-- Price Range -->
        <div class="flex gap-1 w-48">
          <input v-model.number="filters.min_price" placeholder="حداقل قیمت" type="number"
                 @focus="numFocus(filters, 'min_price')" @blur="numBlur(filters, 'min_price')"
                 class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border w-full text-right"
                 @keyup.enter="applyFilters">
          <input v-model.number="filters.max_price" placeholder="حداکثر قیمت" type="number"
                 @focus="numFocus(filters, 'max_price')" @blur="numBlur(filters, 'max_price')"
                 class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border w-full text-right"
                 @keyup.enter="applyFilters">
        </div>

        <!-- Inventory -->
        <div class="flex gap-1 w-48">
          <input v-model.number="filters.min_inventory" placeholder="حداقل موجودی" type="number"
                 @focus="numFocus(filters, 'min_inventory')" @blur="numBlur(filters, 'min_inventory')"
                 class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border w-full text-right"
                 @keyup.enter="applyFilters">
          <input v-model.number="filters.max_inventory" placeholder="حداکثر موجودی" type="number"
                 @focus="numFocus(filters, 'max_inventory')" @blur="numBlur(filters, 'max_inventory')"
                 class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border w-full text-right"
                 @keyup.enter="applyFilters">
        </div>

        <!-- Category -->
        <select @change="applyFilters" v-model="filters.category_tree"
                class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border w-32 text-right">
          <option value="">همه دسته‌ها</option>
          <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
        </select>

        <!-- Brand (Searchable) -->
        <div class="flex items-center gap-1 w-44">
          <input
              v-model="brandSearch"
              placeholder="جستجوی برند"
              class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border w-24 text-right"
              @input="/* reactive filter only */"
          >
          <select @change="applyFilters" v-model="filters.brand_in"
                  class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border flex-1 text-right">
            <option value="">همه برندها</option>
            <option v-for="b in brandsFiltered" :key="b.id" :value="b.id">{{ b.name }}</option>
          </select>
        </div>

        <!-- Currency -->
        <select @change="applyFilters" v-model="filters.currency_in"
                class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border w-32 text-right">
          <option value="">همه ارزها</option>
          <option v-for="cu in currencies" :key="cu.id" :value="cu.id">{{ cu.name }}</option>
          <option value="null">بدون ارز</option>
        </select>

        <!-- Status -->
        <select @change="applyFilters" v-model="filters.status_in"
                class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border w-32 text-right">
          <option value="">همه وضعیت ها</option>
          <option value="pr">نیاز به بررسی</option>
          <option value="pending">در انتظار تایید</option>
          <option value="draft">پیش نویس</option>
          <option value="publish">منتشر شده</option>
        </select>

        <!-- Buttons -->
        <div class="flex gap-1">
          <button @click="resetFilters"
                  class="p-1.5 bg-gradient-to-r from-gray-500 to-blue-600 text-white rounded-full shadow-sm hover:shadow-md transition-all duration-300 transform hover:scale-105"
                  title="حذف فیلترها">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                 stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
            </svg>
          </button>

          <button @click="applyFilters"
                  class="p-1.5 bg-gradient-to-r from-red-500 to-blue-600 text-white rounded-full shadow-sm hover:shadow-md transition-all duration-300 transform hover:scale-105"
                  title="اعمال فیلتر">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                 stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="bg-white bg-opacity-70 backdrop-filter backdrop-blur-lg rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="min-w-full text-sm">
          <thead class="bg-gray-50 bg-opacity-70">
          <tr>
            <th class="max-w-xs p-3 text-center text-xs font-medium text-gray-500">شناسه</th>
            <th class="p-3 text-center text-xs font-medium text-gray-500">SKU</th>
            <th class="p-3 text-center text-xs font-medium text-gray-500">محصول</th>
            <th class="p-3 text-center text-xs font-medium text-gray-500">نوع</th>
            <th class="p-3 text-center text-xs font-medium text-gray-500">قیمت فعلی</th>
            <th class="p-3 text-center text-xs font-medium text-gray-500">موجودی</th>
            <th class="p-3 text-center text-xs font-medium text-gray-500">برند</th>
            <th class="p-3 text-center text-xs font-medium text-gray-500">ارز</th>
            <th class="p-3 text-center text-xs font-medium text-gray-500">قیمت پایه</th>
            <th class="max-w-xs p-3 text-center text-xs font-medium text-gray-500">نوع حاشیه سود</th>
            <th class="p-3 text-center text-xs font-medium text-gray-500">مقدار سود</th>
            <th class="p-3 text-center text-xs font-medium text-gray-500">قیمت محصول</th>
            <th class="max-w-xs p-3 text-center text-xs font-medium text-gray-500">نوع تخفیف</th>
            <th class="p-3 text-center text-xs font-medium text-gray-500">مقدار تخفیف</th>
            <th class="p-3 text-center text-xs font-medium text-gray-500">قیمت فروش</th>
            <th class="p-3 text-center text-xs font-medium text-gray-500"></th>
          </tr>
          </thead>

          <tbody>
          <tr v-if="listLoading">
            <td colspan="16" class="p-4 text-center">
              <div class="flex justify-center items-center gap-2">
                <svg class="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none"
                     viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                  <path class="opacity-75" fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>
                در حال بارگذاری...
              </div>
            </td>
          </tr>

          <tr v-else-if="products.length === 0">
            <td colspan="16" class="p-4 text-center text-gray-500">محصولی یافت نشد</td>
          </tr>

          <tr
              v-for="p in products"
              :key="p.id"
              class="border-t border-gray-100 transition cursor-pointer"
              :class="{'bg-blue-50': selectedRow === p.id}"
              @click="selectedRow = p.id"
          >
            <td class="p-3 text-[10px] text-gray-900 text-center">{{ p.id || '-' }}</td>

            <td class="p-3 text-[10px] text-gray-900 text-center">
              {{ p.sixteen_digit_code || 'بدون کد' }}
            </td>

            <td class="p-3">
              <div class="flex items-center gap-3">
                <div v-if="p.image" class="relative">
                  <img :src="img_source(p.image)" class="w-10 h-10 rounded-lg object-cover border border-gray-200 shadow-sm">
                </div>
                <div>
                  <div class="text-[10px] font-medium text-gray-900 truncate">{{ p.name }}</div>
                  <div v-if="p.product_type==='variation'" class="text-[9px] text-gray-500 mt-1 flex items-center gap-1">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24"
                         stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/>
                    </svg>
                    {{ p.variation_of_name }}
                  </div>
                </div>
              </div>
            </td>

            <td class="p-3 text-center">
              <span class="px-2.5 py-1 rounded-full text-[9px] font-medium"
                    :class="{
                      'bg-green-100 text-green-800': p.product_type==='simple',
                      'bg-blue-100 text-blue-800': p.product_type==='variable',
                      'bg-red-100 text-red-800': p.product_type==='variation'
                    }">
                {{ getTypeLabel(p.product_type) }}
              </span>
            </td>
            <td class="p-3">
              <div class="text-[9px] font-medium text-gray-900">{{ p.price | toMoney }} تومان</div>
              <div v-if="p.regular_price > p.price" class="text-[8px] text-green-600 line-through mt-1">
                {{ p.regular_price | toMoney }}
                تومان
              </div>
            </td>


            <td class="p-3 text-center text-[11px]">
              <span v-if="p.inventory_count > 0">{{ toMoney(p.inventory_count) }}</span>
              <span v-else class="bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-[9px]">اتمام موجودی</span>
            </td>

            <!-- Brand (row - searchable via datalist) -->
            <td class="p-3">
              <input
                  :list="'brandList'"
                  v-model="p._brand_name"
                  @change="onBrandInputChange(p)"
                  placeholder="انتخاب برند"
                  class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border w-32 text-right"
              >
            </td>

            <!-- shared datalist for brands -->
            <datalist id="brandList">
              <option v-for="b in brandsFiltered" :key="b.id" :value="b.name">{{ b.name }}</option>
            </datalist>

            <!-- Currency -->
            <td class="p-3">
              <select
                  v-model="p.currency"
                  @change="onRowChange(p)"
                  class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border w-20 text-right">
                <option :value="null">بدون ارز</option>
                <option v-for="cu in currencies" :key="cu.id" :value="cu.id">{{ cu.name }}</option>
              </select>
            </td>

            <!-- Base price -->
            <td class="p-3">
              <input
                  v-model.number="p.base_price"
                  @input="onRowChange(p)"
                  @focus="numFocus(p, 'base_price')"
                  @blur="numBlur(p, 'base_price')"
                  placeholder="قیمت پایه"
                  type="number"
                  class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border w-28 text-center">
              <div v-if="p._currency_rate && p._currency_rate !== 1"
                   class="mt-1 text-[8px] text-gray-500 text-center ltr">
                ×
                {{ toMoney(p._currency_rate) }}
                =
                {{ toMoney(p._base_after_rate) }}
              </div>
            </td>

            <td class="p-3">
              <select v-model="p.profit_type" @change="onRowChange(p)"
                      class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border w-12 text-right">
                <option value="percent">درصد</option>
                <option value="amount">مبلغ</option>
              </select>
            </td>

            <td class="p-3">
              <input v-model.number="p.profit_margin" @input="onRowChange(p)"
                     @focus="numFocus(p, 'profit_margin')" @blur="numBlur(p, 'profit_margin')"
                     placeholder="مقدار سود" type="number"
                     class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border w-16 text-center">
              <div class="mt-1 text-[8px] text-gray-500 text-center">
                {{ toMoney(p._profit_amount) }}
                تومان
              </div>
            </td>
            <td class="p-3 text-center text-[10px]">
              <span class="font-medium">{{ toMoney(p._price_with_profit) }}</span>
            </td>
            <td class="p-3">
              <select v-model="p.discount_type" @change="onRowChange(p)"
                      class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border w-12 text-right">
                <option value="percent">درصد</option>
                <option value="amount">مبلغ</option>
              </select>
            </td>

            <td class="p-3">
              <input v-model.number="p.discount_margin" @input="onRowChange(p)"
                     @focus="numFocus(p, 'discount_margin')" @blur="numBlur(p, 'discount_margin')"
                     placeholder="مقدار تخفیف" type="number"
                     class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border w-16 text-center">
              <div class="mt-1 text-[8px] text-gray-500 text-center">
                {{ toMoney(p._discount_amount) }}
                تومان
              </div>
            </td>


            <!-- final price (rounded) -->
            <td class="p-3 text-center text-[11px]">
              <span class="font-semibold text-indigo-700">{{ toMoney(p._final_price) }}</span>
            </td>

            <!-- Save button -->
            <td class="p-3 text-center">
              <button
                  @click="saveRow(p)"
                  class="px-3 py-1.5 rounded-lg text-white text-[11px]"
                  :class="rowSaving[p.id] ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'"
                  :disabled="rowSaving[p.id]">
                <span v-if="!rowSaving[p.id]">ثبت</span>
                <span v-else class="inline-flex items-center gap-2">
                  <svg class="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                    <path class="opacity-75" fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                  </svg>
                  در حال ثبت...
                </span>
              </button>
              <div v-if="rowError[p.id]" class="mt-1 text-[11px] text-red-600">{{ rowError[p.id] }}</div>
              <div v-if="rowSaved[p.id]" class="mt-1 text-[11px] text-green-600">ثبت شد</div>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="p-4 border-t border-gray-100 flex flex-col md:flex-row justify-between items-center gap-4">
        <div class="text-sm text-gray-600">
          نمایش {{ (currentPage - 1) * itemsPerPage + 1 }} تا
          {{ Math.min(currentPage * itemsPerPage, totalProducts) }} از
          {{ totalProducts }} محصول
        </div>
        <div class="flex gap-1">
          <button @click="prevPage" :disabled="currentPage === 1"
                  class="p-2 border border-gray-200 rounded-lg bg-white bg-opacity-50 hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                 stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                             d="M9 5l7 7-7 7"/></svg>
          </button>
          <button v-for="page in visiblePages" :key="page" @click="goToPage(page)"
                  class="p-2 text-center border border-gray-200 rounded-full transition-colors duration-200"
                  :class="{
                    'bg-gradient-to-r from-blue-500/60 to-blue-600/50 text-white shadow-md': page === currentPage,
                    'bg-white bg-opacity-50 hover:bg-gray-50': page !== currentPage,
                    'cursor-default': page === '...'
                  }"
                  :disabled="page === '...'">{{ page }}</button>
          <button @click="nextPage" :disabled="currentPage * itemsPerPage >= totalProducts"
                  class="p-2 border border-gray-200 rounded-lg bg-white bg-opacity-50 hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                 stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                             d="M15 19l-7-7 7-7"/></svg>
          </button>
        </div>
      </div>
    </div>
    <div v-if="popupVisible" class="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
      <div
          class="bg-white rounded-xl shadow-lg p-6 w-80 text-center"
          :class="popupType === 'success' ? 'border-green-500' : 'border-red-500'"
      >
        <h2 class="text-lg font-bold mb-4" :class="popupType === 'success' ? 'text-green-600' : 'text-red-600'">
          {{ popupType === 'success' ? 'موفقیت' : 'خطا' }}
        </h2>
        <p class="text-sm text-gray-700 mb-6">{{ popupMessage }}</p>
        <button
            @click="closePopup"
            class="px-4 py-2 rounded-lg text-white"
            :class="popupType === 'success' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'"
        >
          بستن
        </button>
      </div>
    </div>

  </div>

</template>

<script>

export default {
  name: 'NoCurrencyProductsList',
  data() {
    return {
      popupVisible: false,
      popupMessage: '',
      popupType: 'success',
      selectedRow: null,
      listLoading: true,
      products: [],
      totalProducts: 0,
      currentPage: 1,
      itemsPerPage: 20,
      brandSearch: '', // for filtering brand selects
      filters: {
        id_in: '',
        name_search: '',
        sku: '',
        search_value: '',
        min_price: '',
        max_price: '',
        min_inventory: '',
        max_inventory: '',
        category_tree: '',
        brand_in: '',
        status_in: '',
        currency_in: '',
        supplier: null,
      },
      categories: [],
      brands: [],
      currencies: [],
      rowSaving: {},  // { [id]: boolean }
      rowError: {},   // { [id]: string }
      rowSaved: {},   // { [id]: boolean (flash) }
    }
  },
  computed: {
    visiblePages() {
      const totalPages = Math.ceil(this.totalProducts / this.itemsPerPage)
      const range = 1
      const pages = []
      pages.push(1)
      if (this.currentPage - range > 2) pages.push('...')
      for (let i = Math.max(2, this.currentPage - range); i <= Math.min(totalPages - 1, this.currentPage + range); i++) {
        pages.push(i)
      }
      if (this.currentPage + range < totalPages - 1) pages.push('...')
      if (totalPages > 1) pages.push(totalPages)
      return pages
    },
    brandsFiltered() {
      const q = (this.brandSearch || '').toString().trim().toLowerCase()
      if (!q) return this.brands
      return this.brands.filter(b => (b.name || '').toLowerCase().includes(q))
    }
  },
  methods: {
    showPopup(message, type = 'success') {
      this.popupMessage = message
      this.popupType = type
      this.popupVisible = true
    },
    closePopup() {
      this.popupVisible = false
      if (this.popupType === 'success') {
        this.fetchProducts()
      }
    },
    fetchProducts() {
      this.listLoading = true
      const params = {
        limit: this.itemsPerPage,
        offset: (this.currentPage - 1) * this.itemsPerPage,
        ...this.filters
      }
      Object.keys(params).forEach(k => { if (params[k] === '') delete params[k] })

      this.request({
        url: this.endpoint('product/noCurrencyProductSimpleList'),
        method: 'get',
        params,
        success: (data) => {
          this.products = (data.results || []).map(p => ({
            ...p,
            base_price: p.base_price || 0,
            profit_type: p.profit_type || 'percent',
            profit_margin: p.profit_margin || 0,
            discount_type: p.discount_type || 'percent',
            discount_margin: p.discount_margin || 0,
            shipping_cost: p.shipping_cost || 0,
            currency: p.currency || null,
            _price_with_profit: 0,
            _final_price: 0,
            _currency_rate: 1,
            _base_after_rate: 0,
            _profit_amount: 0,
            _discount_amount: 0,
            // helper for searchable brand input
            _brand_name: p.brand_name || p.brand_label || ''
          }))
          this.totalProducts = data.count || 0
          this.listLoading = false
          this.products.forEach(this.recalcRow)
        },
        error: () => {
          this.listLoading = false
        }
      })
    },
    fetchCategories() {
      this.request({
        url: this.endpoint('api/rootCategories'),
        method: 'get',
        success: (data) => { this.categories = data || [] }
      })
    },
    fetchBrands() {
      this.request({
        url: this.endpoint('api/brands'),
        method: 'get',
        success: (data) => {
          this.brands = data || []
          // try to backfill brand names for rows
          this.products.forEach(p => {
            if (!p._brand_name && p.brand != null) {
              const found = this.brands.find(b => b.id === p.brand)
              if (found) p._brand_name = found.name
            }
          })
        }
      })
    },
    fetchCurrency() {
      this.request({
        url: this.endpoint('main/currency'),
        method: 'get',
        success: (data) => { this.currencies = data || [] }
      })
    },

    // ===== UI actions =====
    applyFilters() {
      this.currentPage = 1
      this.fetchProducts()
    },
    resetFilters() {
      this.filters = {
        search_value: '',
        id_in: '',
        sku: '',
        name_search: '',
        min_price: '',
        max_price: '',
        min_inventory: '',
        max_inventory: '',
        category_tree: '',
        brand_in: '',
        currency_in: '',
        status_in: '',
        supplier: null,
      }
      this.brandSearch = ''
      this.applyFilters()
    },
    refreshProducts() { this.fetchProducts() },
    prevPage() {
      if (this.currentPage > 1) { this.currentPage--; this.fetchProducts() }
    },
    nextPage() {
      if (this.currentPage * this.itemsPerPage < this.totalProducts) { this.currentPage++; this.fetchProducts() }
    },
    goToPage(page) { if (page !== '...') { this.currentPage = page; this.fetchProducts() } },

    // ===== Labels/format =====
    getTypeLabel(t) {
      const map = { simple: 'ساده', variable: 'دارای متغیر', variation: 'متغیر' }
      return map[t] || t || ''
    },
    toMoney(n, digits = 0) {
      if (n == null || isNaN(n)) return '0'
      const num = Number(n).toFixed(digits)
      return num.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },

    // generic focus/blur to hide zeros
    numFocus(obj, key) {
      const v = obj[key]
      if (v === 0) this.$set(obj, key, '')
    },
    numBlur(obj, key) {
      let v = obj[key]
      if (v === '' || v == null || isNaN(Number(v))) {
        this.$set(obj, key, 0)
      } else {
        this.$set(obj, key, Number(v))
      }
    },

    onRowChange(p) {
      this.clearRowFlags(p.id)
      this.recalcRow(p)
    },
    recalcRow(p) {
      // نرخ ارز:
      let currency_rate = 1
      if (p.currency) {
        const c = this.currencies.find(x => x.id === p.currency)
        if (c && c.exchange_rate_to_toman != null) {
          currency_rate = Math.round(Number(c.exchange_rate_to_toman))
        }
      } else if (p.product_type === 'variation' && p.variation_of_currency_rate) {
        currency_rate = Math.round(Number(p.variation_of_currency_rate))
      }

      const base = Number(p.base_price || 0)
      const base_after_rate = base * currency_rate
      const shipping = Number(p.shipping_cost || 0)

      let profit_amount = 0
      if ((p.profit_type || 'percent') === 'percent') {
        profit_amount = base_after_rate * (Number(p.profit_margin || 0) / 100)
      } else {
        profit_amount = Number(p.profit_margin || 0)
      }

      let price_with_profit = base_after_rate + profit_amount + shipping

      let discount_amount = 0
      if ((p.discount_type || 'percent') === 'percent') {
        discount_amount = price_with_profit * (Number(p.discount_margin || 0) / 100)
      } else {
        discount_amount = Number(p.discount_margin || 0)
      }

      const final_amount = price_with_profit - discount_amount

      // set live fields for UI:
      p._currency_rate = currency_rate
      p._base_after_rate = Math.max(0, Math.floor(base_after_rate))
      p._profit_amount = Math.max(0, Math.floor(profit_amount))
      p._discount_amount = Math.max(0, Math.floor(discount_amount))
      p._price_with_profit = Math.max(0, Math.floor(price_with_profit))
      p._final_price = this.roundUpToHundred(final_amount)
    },
    roundUpToHundred(val) {
      if (!val || isNaN(val)) return 0
      const n = Math.ceil(Number(val) / 100) * 100
      return n
    },

    // ===== Save row =====
    clearRowFlags(id) {
      this.$set(this.rowError, id, '')
      this.$set(this.rowSaved, id, false)
    },
    saveRow(p) {
      this.clearRowFlags(p.id)
      this.$set(this.rowSaving, p.id, true)

      const payload = {
        brand: p.brand || null,
        currency: p.currency || null,
        product_type: p.product_type || null,
        profit_type: p.profit_type || 'percent',
        profit_margin: p.profit_margin || 0,
        discount_type: p.discount_type || 'percent',
        discount_margin: p.discount_margin || 0,
        base_price: p.base_price || 0,
        profit_amount: p.profit_type === 'percent'
            ? ( (Number(p.base_price||0) * (this.getCurrencyRate(p) || 1)) * (Number(p.profit_margin||0)/100) )
            : Number(p.profit_margin||0),
        discount_amount: p.discount_type === 'percent'
            ? ( ( (Number(p.base_price||0) * (this.getCurrencyRate(p)||1)) + Number(p.shipping_cost||0) +
                (p.profit_type==='percent'
                    ? ((Number(p.base_price||0) * (this.getCurrencyRate(p)||1)) * (Number(p.profit_margin||0)/100))
                    : Number(p.profit_margin||0)) ) * (Number(p.discount_margin||0)/100) )
            : Number(p.discount_margin||0),
      }

      this.request({
        url: this.endpoint(`product/productPricingMeta/${p.id}`),
        method: 'patch',
        data: payload,
        success: () => {
          this.showPopup('ثبت با موفقیت انجام شد ✅', 'success')
          this.$set(this.rowSaving, p.id, false)
        },
        error: (err) => {
          const data = err?.response?.data
          const msg = data?.detail || Object.values(data || {}).flat().join('، ') || 'خطا در ثبت'
          this.showPopup(msg, 'error')
          this.$set(this.rowSaving, p.id, false)
        }
      })
    },
    getCurrencyRate(p) {
      if (p.currency) {
        const c = this.currencies.find(x => x.id === p.currency)
        if (c && c.exchange_rate_to_toman != null) return Math.round(Number(c.exchange_rate_to_toman))
      } else if (p.product_type === 'variation' && p.variation_of_currency_rate) {
        return Math.round(Number(p.variation_of_currency_rate))
      }
      return 1
    },

    // map typed brand name -> brand id, keep logic intact
    onBrandInputChange(p) {
      const name = (p._brand_name || '').toString().trim().toLowerCase()
      const found = this.brands.find(b => (b.name || '').toLowerCase() === name)
      p.brand = found ? found.id : null
    }
  },

  mounted() {
    // فیلترهای ورودی از QueryString
    for (let param in this.$route.query) {
      if (param === 'brand') this.filters.brand_in = this.$route.query['brand']
      if (param === 'currency') this.filters.currency_in = this.$route.query['currency']
      if (param === 'supplier') this.filters.supplier = this.$route.query['supplier']
    }
    this.fetchProducts()
    this.fetchBrands()
    this.fetchCategories()
    this.fetchCurrency()
  }
}
</script>

<style>
body { direction: rtl; text-align: right; }

.truncate-text { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100px; }

.transition-all { transition-property: all; transition-timing-function: cubic-bezier(0.4,0,0.2,1); transition-duration: 150ms; }

.bg-secondary { background-color: #424242; }
</style>
